import React, { useState } from 'react';
import { useParams, Link } from 'react-router';
import { PageHero } from '../components/PageHero';
import { Section } from '../components/Section';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { PlaceholderImage } from '../components/PlaceholderImage';
import { Modal } from '../components/Modal';
import { events } from './Home/data/events';

export const EventDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [isRSVPModalOpen, setIsRSVPModalOpen] = useState(false);
  
  const event = events.find((e) => e.slug === slug);

  if (!event) {
    return (
      <>
        <PageHero
          title="Event Not Found"
          subtitle="The event you're looking for doesn't exist."
          breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Events', path: '/events' }, { label: 'Not Found' }]}
        />
        <Section>
          <Card>
            <p style={{ color: 'var(--muted)', textAlign: 'center' }}>
              <Link to="/events" style={{ color: 'var(--g1)', textDecoration: 'none' }}>
                Return to Events
              </Link>
            </p>
          </Card>
        </Section>
      </>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getFormatBadgeColor = (format: string) => {
    switch (format) {
      case 'Virtual':
        return 'var(--accentK)';
      case 'In-person':
        return 'var(--g1)';
      case 'Hybrid':
        return 'var(--accentE)';
      default:
        return 'var(--muted)';
    }
  };

  return (
    <>
      <PageHero
        title={event.title}
        subtitle={`${formatDate(event.date)} • ${event.location} • ${event.format}`}
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Events', path: '/events' },
          { label: event.title },
        ]}
      />

      <Section>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '32px',
          }}
          className="eventDetailGrid"
        >
          <div style={{ gridColumn: 'span 2' }}>
            <div
              style={{
                marginBottom: '32px',
              }}
            >
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--text)',
                  padding: '6px 12px',
                  backgroundColor: getFormatBadgeColor(event.format),
                  borderRadius: '6px',
                  display: 'inline-block',
                  marginBottom: '16px',
                }}
              >
                {event.format}
              </span>
              <p
                style={{
                  color: 'var(--muted)',
                  lineHeight: 1.6,
                  fontSize: '16px',
                }}
              >
                {event.description}
              </p>
            </div>

            {event.agenda && event.agenda.length > 0 && (
              <Card style={{ marginBottom: '32px' }}>
                <h3
                  style={{
                    fontSize: '22px',
                    fontWeight: 600,
                    color: 'var(--text)',
                    marginBottom: '20px',
                  }}
                >
                  Agenda
                </h3>
                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                  }}
                >
                  {event.agenda.map((item, idx) => (
                    <li
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        color: 'var(--text)',
                        fontSize: '16px',
                        lineHeight: 1.6,
                      }}
                    >
                      <span
                        style={{
                          color: 'var(--g1)',
                          marginTop: '4px',
                          flexShrink: 0,
                          fontWeight: 600,
                        }}
                      >
                        {idx + 1}.
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {event.speakers && event.speakers.length > 0 && (
              <Card>
                <h3
                  style={{
                    fontSize: '22px',
                    fontWeight: 600,
                    color: 'var(--text)',
                    marginBottom: '20px',
                  }}
                >
                  Speakers
                </h3>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '16px',
                  }}
                >
                  {event.speakers.map((speaker, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: '16px',
                        backgroundColor: 'var(--panel)',
                        borderRadius: 'var(--r1)',
                        border: '1px solid var(--line)',
                      }}
                    >
                      <PlaceholderImage
                        label={`${speaker.name} Photo`}
                        height={120}
                        style={{ marginBottom: '12px' }}
                      />
                      <div
                        style={{
                          fontSize: '16px',
                          fontWeight: 600,
                          color: 'var(--text)',
                          marginBottom: '4px',
                        }}
                      >
                        {speaker.name}
                      </div>
                      <div
                        style={{
                          fontSize: '13px',
                          color: 'var(--muted)',
                        }}
                      >
                        {speaker.title}
                      </div>
                      <div
                        style={{
                          fontSize: '13px',
                          color: 'var(--g1)',
                        }}
                      >
                        {speaker.company}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>

          <div>
            <Card style={{ position: 'sticky', top: '100px' }}>
              <h3
                style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: 'var(--text)',
                  marginBottom: '20px',
                }}
              >
                Event Details
              </h3>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  marginBottom: '24px',
                  color: 'var(--muted)',
                  fontSize: '14px',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: '4px' }}>
                    Date
                  </div>
                  {formatDate(event.date)}
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: '4px' }}>
                    Location
                  </div>
                  {event.location}
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: '4px' }}>
                    Format
                  </div>
                  {event.format}
                </div>
              </div>
              <Button
                variant="primary"
                onClick={() => setIsRSVPModalOpen(true)}
                style={{ width: '100%', marginBottom: '16px' }}
              >
                RSVP
              </Button>
              <Link to="/events" style={{ textDecoration: 'none', display: 'block' }}>
                <Button variant="secondary" style={{ width: '100%' }}>
                  Back to Events
                </Button>
              </Link>

              <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid var(--line)' }}>
                <h4
                  style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: 'var(--text)',
                    marginBottom: '16px',
                  }}
                >
                  Related Opportunities
                </h4>
                <Badge status="COMING SOON" />
                <div
                  style={{
                    marginTop: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                  }}
                >
                  {[
                    'Korean wastewater solutions partner search',
                    'Egyptian industrial supplier matchmaking',
                  ].map((opp, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: '12px',
                        backgroundColor: 'var(--panel)',
                        borderRadius: 'var(--r1)',
                        border: '1px solid var(--line)',
                        fontSize: '14px',
                        color: 'var(--muted)',
                      }}
                    >
                      {opp}
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </div>
        </div>
      </Section>

      <Modal
        isOpen={isRSVPModalOpen}
        onClose={() => setIsRSVPModalOpen(false)}
        title="RSVP Coming Soon"
      >
        <p style={{ color: 'var(--muted)', lineHeight: 1.6 }}>
          Event RSVP functionality will be available in Phase 2 of the BCBH portal. For now, please contact us directly to express your interest in attending events.
        </p>
        <div style={{ marginTop: '24px' }}>
          <Button variant="primary" onClick={() => setIsRSVPModalOpen(false)}>
            Got it
          </Button>
        </div>
      </Modal>
    </>
  );
};

